import { useEffect, useState } from "react";
import {
  getTrainingSessionCompanionFiles,
  getTrainingSessionRawCheckpoints,
  type CompanionFileView,
  type RawCheckpointView,
} from "@/api";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset";

// 탭 진입·창 복원·수동 갱신은 세 목록(exported·Raw·부속 파일)을 함께 읽는다. Raw 와 부속 파일은
// 폴링하지 않는다. 떠난 탭의 응답이 다시 진입한 결과를 덮지 못하도록 요청 수명에 연결한다.
export function useSessionCheckpoints(
  sessionId: string,
  active: boolean,
  refreshExported: () => Promise<void>,
) {
  const [rawState, setRawState] = useState<LoadState<RawCheckpointView[]>>({ status: "idle" });
  const [companionState, setCompanionState] = useState<LoadState<CompanionFileView[]>>({
    status: "idle",
  });
  const [exportedRefreshing, setExportedRefreshing] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!active) return;
    const request = new AbortController();
    // 한 목록의 실패가 다른 목록을 가리지 않도록 목록마다 상태를 따로 둔다.
    function load<T>(
      fetch: (id: string, options: { signal: AbortSignal }) => Promise<T>,
      setState: (state: LoadState<T>) => void,
    ): void {
      setState({ status: "loading" });
      fetch(sessionId, { signal: request.signal }).then(
        (data) => {
          if (!request.signal.aborted) setState({ status: "success", data });
        },
        (error: unknown) => {
          if (!request.signal.aborted) {
            setState({ status: "error", message: resolveLoadErrorMessage(error) });
          }
        },
      );
    }
    load(getTrainingSessionRawCheckpoints, setRawState);
    load(getTrainingSessionCompanionFiles, setCompanionState);
    setExportedRefreshing(true);
    void refreshExported().finally(() => {
      if (!request.signal.aborted) setExportedRefreshing(false);
    });
    return () => request.abort();
  }, [sessionId, active, refreshTick, refreshExported]);

  return {
    rawState,
    companionState,
    exportedRefreshing,
    refresh: () => setRefreshTick((tick) => tick + 1),
  };
}
